import arrowLeft from "../../../../images/arrow_left.svg";
import Button from "components/ui/Button";
import { ComponentType, ReactNode, useState } from "react";
import "./SidePanelSection.css";
import classnames from "classnames";
import { useTranslation } from "react-i18next";
import { Location } from "react-router-dom";

const REDUCED_LIST_SIZE = 3;

type Props<TItem> = {
  list: TItem[];
  tagName: ComponentType<SidePanelRowProps<TItem>>;
  sectionTitle: ReactNode;
  isReducedList?: boolean;
  showBackArrow?: boolean;
  collapse?: () => void;
  // A lesson panel lists `sections` rather than `list`, but `list` still decides
  // whether the section renders at all.
  isLessonPage?: boolean;
  sections?: TItem[];
  location?: Location;
  open?: boolean;
  data?: unknown;
  fetchLoading?: boolean;
  assignments?: unknown;
  fileName?: string;
  refresh?: unknown;
  onSectionActive?: (id: string) => void;
};

// Each row receives the whole context bag whether or not it reads any of it, so
// every field a caller does not pass arrives as undefined.
type SidePanelRowProps<TItem> = {
  item: TItem;
  location?: Location;
  open?: boolean;
  data?: unknown;
  loading?: boolean;
  assignments?: unknown;
  fileName?: string;
  refresh?: unknown;
  isLessonItem?: boolean;
  onSectionActive?: (id: string) => void;
};

export default function SidePanelSection<TItem>(props: Props<TItem>) {
  const { t } = useTranslation();
  const [list, setList] = useState(
    props.isReducedList ? props.list.slice(0, REDUCED_LIST_SIZE) : props.list
  );
  const [allIsVisible, setAllIsVisible] = useState(!props.isReducedList);

  const showAll = () => {
    setAllIsVisible(true);
    setList(props.list);
  };

  if (props.list.length === 0) {
    return null;
  }

  const TagName = props.tagName;
  const rows = props.isLessonPage ? (props.sections ?? []) : list;

  return (
    <div className="side-panel-section">
      {props.showBackArrow && (
        <span
          onClick={props.collapse}
          className={classnames("side-panel-arrow", { link: props.collapse })}
        >
          <img src={arrowLeft} alt="arrow-left" />
          {t("Common.actions.back")}
        </span>
      )}
      {!props.isLessonPage && (
        <div className="side-panel-title">{props.sectionTitle}</div>
      )}
      <ul>
        {rows.map((item, index) => (
          <TagName
            key={index}
            item={item}
            open={props.open}
            data={props.data}
            loading={props.fetchLoading}
            assignments={props.assignments}
            fileName={props.fileName}
            location={props.location}
            refresh={props.refresh}
            isLessonItem={props.isLessonPage}
            onSectionActive={props.isLessonPage ? props.onSectionActive : undefined}
          />
        ))}
      </ul>
      {!allIsVisible && props.list.length > REDUCED_LIST_SIZE && (
        <Button variant="plain" className="show-more" onClick={showAll}>
          {`${t("Common.actions.showMore")} (${props.list.length - REDUCED_LIST_SIZE})`}
        </Button>
      )}
    </div>
  );
}
